import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ShoppingBag,
  Search,
  Edit3,
  Trash2,
  Eye,
  Plus,
  RefreshCw,
  Filter,
  Building2,
  MapPin,
  DollarSign,
  Save,
  X,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'

interface MarketplaceProperty {
  id: string
  name: string
  type: 'DST' | 'Direct'
  sponsor: string
  location: string
  price: number
  minInvestment: number
  expectedReturn: number
  description: string
  features: string[]
  isQualified: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export const AdminMarketplacePage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const [properties, setProperties] = useState<MarketplaceProperty[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedProperty, setSelectedProperty] = useState<MarketplaceProperty | null>(null)
  const [editingProperty, setEditingProperty] = useState<MarketplaceProperty | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editForm, setEditForm] = useState<Partial<MarketplaceProperty>>({})

  useEffect(() => {
    if (!currentUser?.isAdmin) {
      navigate('/app')
      return
    }
    loadProperties()
  }, [currentUser, navigate, page, searchQuery, typeFilter, statusFilter])

  const loadProperties = async () => {
    try {
      setIsLoading(true)
      // Mock data for now - replace with actual API call
      const mockProperties: MarketplaceProperty[] = [
        {
          id: '1',
          name: 'Ares Industrial Portfolio',
          type: 'DST',
          sponsor: 'ARES',
          location: 'Dallas, TX',
          price: 50000000,
          minInvestment: 50000,
          expectedReturn: 6.5,
          description: 'Diversified industrial portfolio across major US markets with strong tenant credit profiles.',
          features: ['Industrial', 'Multi-tenant', 'Grade A', 'Long-term leases'],
          isQualified: true,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: '2',
          name: 'JLL Office Tower',
          type: 'Direct',
          sponsor: 'JLL',
          location: 'Austin, TX',
          price: 25000000,
          minInvestment: 100000,
          expectedReturn: 7.2,
          description: 'Class A office building in downtown Austin with premium amenities and strong occupancy.',
          features: ['Office', 'Class A', 'Downtown', 'Premium amenities'],
          isQualified: true,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: '3',
          name: 'Inland Retail Center',
          type: 'DST',
          sponsor: 'Inland',
          location: 'Phoenix, AZ',
          price: 35000000,
          minInvestment: 25000,
          expectedReturn: 5.8,
          description: 'Well-located retail center with national tenants and strong foot traffic.',
          features: ['Retail', 'National tenants', 'High traffic', 'Stable income'],
          isQualified: true,
          isActive: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ]

      // Filter mock data
      let filtered = mockProperties.filter(p => {
        const matchesSearch = !searchQuery || 
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.sponsor.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesType = !typeFilter || p.type === typeFilter
        const matchesStatus = !statusFilter || 
          (statusFilter === 'active' && p.isActive) ||
          (statusFilter === 'inactive' && !p.isActive) ||
          (statusFilter === 'qualified' && p.isQualified) ||
          (statusFilter === 'unqualified' && !p.isQualified)
        return matchesSearch && matchesType && matchesStatus
      })

      setProperties(filtered)
      setTotalPages(1)
    } catch (error) {
      console.error('Failed to load properties:', error)
      toast.error('Failed to load marketplace properties')
    } finally {
      setIsLoading(false)
    }
  }

  const handleViewProperty = (property: MarketplaceProperty) => {
    setSelectedProperty(property)
  }

  const handleEditProperty = (property: MarketplaceProperty) => {
    setEditingProperty(property)
    setEditForm({
      name: property.name,
      type: property.type,
      sponsor: property.sponsor,
      location: property.location,
      price: property.price,
      minInvestment: property.minInvestment,
      expectedReturn: property.expectedReturn,
      description: property.description,
      features: property.features,
      isQualified: property.isQualified,
      isActive: property.isActive
    })
  }

  const handleSaveProperty = async () => {
    if (!editingProperty) return

    try {
      // Mock save - replace with actual API call
      toast.success('Property updated successfully')
      setEditingProperty(null)
      loadProperties()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update property')
    }
  }

  const handleDeleteProperty = async (propertyId: string) => {
    if (!confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
      return
    }

    try {
      // Mock delete - replace with actual API call
      toast.success('Property deleted successfully')
      loadProperties()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete property')
    }
  }

  const handleToggleActive = async (property: MarketplaceProperty) => {
    try {
      // Mock toggle - replace with actual API call
      toast.success(`Property ${property.isActive ? 'deactivated' : 'activated'} successfully`)
      loadProperties()
    } catch (error: any) {
      toast.error('Failed to update property status')
    }
  }

  const getStatusBadge = (property: MarketplaceProperty) => {
    if (!property.isActive) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200">
          <XCircle className="h-3 w-3 mr-1" />
          Inactive
        </span>
      )
    }
    if (property.isQualified) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          Qualified
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
        <Clock className="h-3 w-3 mr-1" />
        Pending
      </span>
    )
  }

  if (isLoading && properties.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-3">
            <ShoppingBag className="h-8 w-8 text-primary-600" />
            Marketplace Management
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage all marketplace properties and listings
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Property
          </Button>
          <Button onClick={loadProperties} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search properties by name, location, or sponsor..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(1)
              }}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-slate-400" />
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value)
                setPage(1)
              }}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            >
              <option value="">All Types</option>
              <option value="DST">DST</option>
              <option value="Direct">Direct</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPage(1)
              }}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="qualified">Qualified</option>
              <option value="unqualified">Unqualified</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Properties Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Property
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700">
              {properties.map((property) => (
                <tr key={property.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingProperty?.id === property.id ? (
                      <Input
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full"
                        placeholder="Property Name"
                      />
                    ) : (
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {property.name}
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {property.sponsor}
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingProperty?.id === property.id ? (
                      <select
                        value={editForm.type || ''}
                        onChange={(e) => setEditForm({ ...editForm, type: e.target.value as 'DST' | 'Direct' })}
                        className="px-3 py-1 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm"
                      >
                        <option value="DST">DST</option>
                        <option value="Direct">Direct</option>
                      </select>
                    ) : (
                      <span className="text-sm text-slate-900 dark:text-slate-100">{property.type}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingProperty?.id === property.id ? (
                      <Input
                        value={editForm.location || ''}
                        onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                        className="w-full"
                        placeholder="Location"
                      />
                    ) : (
                      <div className="flex items-center text-sm text-slate-900 dark:text-slate-100">
                        <MapPin className="h-4 w-4 mr-1 text-slate-400" />
                        {property.location}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingProperty?.id === property.id ? (
                      <div className="space-y-2">
                        <Input
                          type="number"
                          value={editForm.price || ''}
                          onChange={(e) => setEditForm({ ...editForm, price: Number(e.target.value) })}
                          className="w-full"
                          placeholder="Price"
                        />
                        <Input
                          type="number"
                          value={editForm.minInvestment || ''}
                          onChange={(e) => setEditForm({ ...editForm, minInvestment: Number(e.target.value) })}
                          className="w-full"
                          placeholder="Min Investment"
                        />
                      </div>
                    ) : (
                      <div className="text-sm text-slate-900 dark:text-slate-100">
                        <div className="font-medium">${(property.price / 1000000).toFixed(1)}M</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          Min: ${property.minInvestment.toLocaleString()}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingProperty?.id === property.id ? (
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={editForm.isActive || false}
                            onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                            className="rounded"
                          />
                          <span>Active</span>
                        </label>
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={editForm.isQualified || false}
                            onChange={(e) => setEditForm({ ...editForm, isQualified: e.target.checked })}
                            className="rounded"
                          />
                          <span>Qualified</span>
                        </label>
                      </div>
                    ) : (
                      getStatusBadge(property)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                    {new Date(property.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {editingProperty?.id === property.id ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSaveProperty}
                            className="text-green-600 hover:text-green-700 dark:text-green-400"
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingProperty(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewProperty(property)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditProperty(property)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleActive(property)}
                            className={property.isActive ? 'text-yellow-600 hover:text-yellow-700' : 'text-green-600 hover:text-green-700'}
                          >
                            {property.isActive ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteProperty(property.id)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Property Details Modal */}
      {selectedProperty && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  Property Details
                </h2>
                <Button variant="outline" onClick={() => setSelectedProperty(null)}>
                  Close
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Name</label>
                  <p className="text-slate-900 dark:text-slate-100">{selectedProperty.name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Type</label>
                    <p className="text-slate-900 dark:text-slate-100">{selectedProperty.type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Sponsor</label>
                    <p className="text-slate-900 dark:text-slate-100">{selectedProperty.sponsor}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Location</label>
                  <p className="text-slate-900 dark:text-slate-100">{selectedProperty.location}</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Price</label>
                    <p className="text-slate-900 dark:text-slate-100">${(selectedProperty.price / 1000000).toFixed(1)}M</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Min Investment</label>
                    <p className="text-slate-900 dark:text-slate-100">${selectedProperty.minInvestment.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Expected Return</label>
                    <p className="text-slate-900 dark:text-slate-100 text-green-600">{selectedProperty.expectedReturn}%</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Description</label>
                  <p className="text-slate-900 dark:text-slate-100">{selectedProperty.description}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Features</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedProperty.features.map((feature, index) => (
                      <span key={index} className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-sm">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Status</label>
                  <div className="mt-2">{getStatusBadge(selectedProperty)}</div>
                </div>
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700 flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleEditProperty(selectedProperty)
                      setSelectedProperty(null)
                    }}
                  >
                    <Edit3 className="h-4 w-4 mr-2" />
                    Edit Property
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleToggleActive(selectedProperty)}
                  >
                    {selectedProperty.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Add Property Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  Add New Property
                </h2>
                <Button variant="outline" onClick={() => setShowAddModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-4">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Property creation form would go here. This is a placeholder for the add property functionality.
                </p>
                <div className="flex gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <Button onClick={() => {
                    toast.success('Property creation form would be implemented here')
                    setShowAddModal(false)
                  }}>
                    Create Property
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}




